import React, { useState } from "react";
import { Loader2, FileText, Paperclip, History } from "lucide-react";
import { useAuth, highestRole, canEditAssociation } from "../contexts/AuthContext";
import AttachmentHoverPreview from "./AttachmentHoverPreview";
import { PlanYearPill } from "./PlanYearPill";
import AuditFeedDrawer from "./AuditFeedDrawer";

// Association-tab blue header: New Plan Year + Reserve Budget Report (left),
// Attachments + Activity Audit (right). All pills share one width.
const PILL_CLS = "flex items-center justify-center gap-1 py-1 text-[11px] font-semibold rounded-[3px] transition-colors hover:bg-white/[0.12]";
const PILL_STYLE = { width: 162, border: "1px solid rgba(255,255,255,0.55)", color: "#fff" };

function ReserveBudgetReportButton({ association, onOpen }) {
  if (!association) return null;
  const commonProps = {
    className: PILL_CLS,
    style: PILL_STYLE,
    "data-testid": "reserve-budget-report-btn",
    title: "Open the Reserve Budget Report (plain-language digest, live data)",
  };
  if (typeof onOpen === "function") {
    return (
      <button type="button" onClick={onOpen} {...commonProps}>
        <FileText size={12} />
        Reserve Budget Report
      </button>
    );
  }
  return (
    <a href={`#report/reserve-budget/${association.aid}`} target="_blank" rel="noopener noreferrer" {...commonProps}>
      <FileText size={12} />
      Reserve Budget Report
    </a>
  );
}

function LeftPills({ association, onPlanYearChanged, onOpenReport }) {
  return (
    <>
      <PlanYearPill
        association={association}
        onChanged={onPlanYearChanged}
        label="New Plan Year"
        testId="assoc-new-plan-year-pill"
        className={PILL_CLS}
        style={PILL_STYLE}
        iconFirst
      />
      <ReserveBudgetReportButton association={association} onOpen={onOpenReport} />
    </>
  );
}

function ActivityAuditPill({ association }) {
  const { user } = useAuth();
  const [auditOpen, setAuditOpen] = useState(false);
  if (!canEditAssociation(user, association?.tenant_id, association?.aid)) return null;
  return (
    <>
      <button
        onClick={() => setAuditOpen(true)}
        data-testid="assoc-audit-open"
        className={PILL_CLS}
        style={PILL_STYLE}
        title="View audit feed for this association"
      >
        <History size={12} /> Activity Audit
      </button>
      {auditOpen && <AuditFeedDrawer association={association} onClose={() => setAuditOpen(false)} />}
    </>
  );
}

export default function ControlsStrip({
  association,
  variables,
  onOpenAssociationAttachments,
  onOpenReport,
  onPlanYearChanged,
  associationAttachmentCount,
  running,
  currentScreen,
}) {
  const { user } = useAuth();
  const isAdmin = highestRole(user) === "administrator";
  if (!association || !variables) return null;

  return (
    <div style={{ marginTop: 6 }} data-testid="controls-strip-wrap">
      <div style={{ borderTop: "2px solid #EA6A47", marginBottom: 6 }} data-testid="controls-strip-coral-rule" />
      <div
        className="border border-navy flex items-stretch text-[12px] rounded-[3px] overflow-hidden relative"
        style={{ background: "#1C4E80" }}
        data-testid="controls-strip"
      >
        <div className="flex-1 min-w-0 flex items-center gap-2 px-3 py-1.5" data-testid="controls-strip-left">
          {currentScreen === "Association" && (
            <LeftPills association={association} onPlanYearChanged={onPlanYearChanged} onOpenReport={onOpenReport} />
          )}
        </div>

        <div className="flex items-center gap-2 px-3 py-1.5 justify-end relative shrink-0">
          {running && (
            <span
              className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-[3px]"
              style={{ border: "1px solid rgba(255,255,255,0.55)", background: "transparent", color: "#fff" }}
              data-testid="controls-recalc-indicator"
              title="Waiting for the projection engine to recompute — your slider change was registered."
            >
              <Loader2 size={12} className="animate-spin" /> Recalculating…
            </span>
          )}
          {isAdmin && currentScreen === "Association" && (
            <AttachmentHoverPreview
              parent={{ type: "association", id: association?.aid }}
              count={associationAttachmentCount || 0}
              side="bottom"
              wrapperClass="relative inline-block"
              testid="assoc-attachments-hover"
            >
              <button
                onClick={onOpenAssociationAttachments}
                className={PILL_CLS}
                style={PILL_STYLE}
                data-testid="assoc-attachments-btn"
                title={
                  associationAttachmentCount > 0
                    ? "Open Attachments · hover to peek at files"
                    : "Attach files (JPEG, PNG, PDF, Word, Excel, or CSV) to this Association"
                }
              >
                <Paperclip size={12} />
                Attachments
                {associationAttachmentCount > 0 && (
                  <span className="ml-0.5 text-[10px] opacity-90">({associationAttachmentCount})</span>
                )}
              </button>
            </AttachmentHoverPreview>
          )}
          {currentScreen === "Association" && (
            <ActivityAuditPill association={association} />
          )}
        </div>
      </div>
    </div>
  );
}
